import { useSignal } from "@preact/signals";
import type { Channel } from "~/web/src/channel";
import { useFile } from "~/web/src/project/use-file";

// A text file open for editing, raw: the whole file in one textarea, Apply
// writes it. Disk tracking (versions, conflicts, changes underneath) is useFile's.

export function TextTab({ channel, name }: { channel: Channel; name: string }) {
  // null = no edits, the textarea follows the disk (first load, reload, a change on disk).
  const draft = useSignal<string | null>(null);
  const file = useFile(channel, name, () => isDirty(draft.value, file.disk.value));
  const dirty = isDirty(draft.value, file.disk.value);
  const text = draft.value ?? file.disk.value;

  async function apply() {
    if (await file.write(text)) draft.value = null;
  }

  return (
    <div class="editor">
      <textarea
        value={text}
        spellcheck={false}
        onInput={(e) => {
          draft.value = e.currentTarget.value;
        }}
      />
      <div class="editor-bar">
        <span class="pane-hint">
          {name} · v{file.version.value}
          {dirty ? " · edited" : ""}
        </span>
        {file.notice.value && <span class="notice">{file.notice.value}</span>}
        <span style="display:flex;gap:6px">
          <button
            type="button"
            class="btn ghost sm"
            onClick={() => {
              draft.value = null;
              void file.load();
            }}
            disabled={!dirty || file.busy.value}
          >
            Reload
          </button>
          <button
            type="button"
            class="btn sm"
            onClick={() => void apply()}
            disabled={!dirty || file.busy.value}
          >
            Apply
          </button>
        </span>
      </div>
    </div>
  );
}

const isDirty = (draft: string | null, disk: string) => draft !== null && draft !== disk;
